import type { ChangeEvent, PointerEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { 
  EMAIL_REGEX, 
  PASSWORD_REGEX, 
  MIN_PASSWORD_LENGTH,
  validateEmail as validateEmailUtil,
  validatePassword as validatePasswordUtil,
} from '@/lib/utils/authValidation';

// Re-export for backward compatibility
export { EMAIL_REGEX, PASSWORD_REGEX, MIN_PASSWORD_LENGTH };

export interface RippleState {
  x: number;
  y: number;
  active: boolean;
}

interface UseAuthFormOptions {
  debounceMs?: number;
  withPassword?: boolean;
  emailErrorMessage?: string;
  passwordErrorMessage?: string;
}

export function useAuthForm({
  debounceMs = 400,
  withPassword = true,
  emailErrorMessage = 'Проверьте, правильно ли указан email.',
  passwordErrorMessage = 'Минимум 8 символов, буквы и цифры.',
}: UseAuthFormOptions = {}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const emailDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const passwordDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [ripple, setRipple] = useState<RippleState>({ x: 0, y: 0, active: false });

  useEffect(() => () => {
    if (emailDebounceRef.current) {
      clearTimeout(emailDebounceRef.current);
    }
    if (passwordDebounceRef.current) {
      clearTimeout(passwordDebounceRef.current);
    }
  }, []);

  const validateEmail = useCallback((value: string): boolean => {
    if (!value) return true;
    return validateEmailUtil(value);
  }, []);

  const validatePassword = useCallback(
    (value: string): boolean => {
      if (!withPassword) return true;
      if (!value) return true;
      return validatePasswordUtil(value);
    },
    [withPassword]
  );

  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setEmail(value);
      setEmailError(null);

      if (emailDebounceRef.current) {
        clearTimeout(emailDebounceRef.current);
      }

      emailDebounceRef.current = setTimeout(() => {
        if (value && !validateEmail(value)) {
          setEmailError(emailErrorMessage);
        }
      }, debounceMs);
    },
    [debounceMs, emailErrorMessage, validateEmail]
  );

  const handleEmailBlur = useCallback(() => {
    setIsEmailFocused(false);
    if (email && !validateEmail(email)) {
      setEmailError(emailErrorMessage);
    }
  }, [email, emailErrorMessage, validateEmail]);

  const handleEmailFocus = useCallback(() => {
    setIsEmailFocused(true);
    setEmailError(null);
  }, []);

  const handlePasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setPassword(value);
      setPasswordError(null);

      if (!withPassword) return;

      if (passwordDebounceRef.current) {
        clearTimeout(passwordDebounceRef.current);
      }

      passwordDebounceRef.current = setTimeout(() => {
        if (value && !validatePassword(value)) {
          setPasswordError(passwordErrorMessage);
        }
      }, debounceMs);
    },
    [debounceMs, passwordErrorMessage, validatePassword, withPassword]
  );

  const handlePasswordBlur = useCallback(() => {
    setIsPasswordFocused(false);
    if (!withPassword) return;

    if (password && !validatePassword(password)) {
      setPasswordError(passwordErrorMessage);
    }
  }, [password, passwordErrorMessage, validatePassword, withPassword]);

  const handlePasswordFocus = useCallback(() => {
    setIsPasswordFocused(true);
    setPasswordError(null);
  }, []);

  const triggerRipple = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      const button = buttonRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setRipple({ x, y, active: true });
    },
    []
  );

  const resetRipple = useCallback(() => {
    setRipple(prev => (prev.active ? { ...prev, active: false } : prev));
  }, []);

  return {
    email,
    password,
    setEmail,
    setPassword,
    setEmailError,
    setPasswordError,
    emailError,
    passwordError,
    isEmailFocused,
    isPasswordFocused,
    handleEmailChange,
    handleEmailBlur,
    handleEmailFocus,
    handlePasswordChange,
    handlePasswordBlur,
    handlePasswordFocus,
    validateEmail,
    validatePassword,
    triggerRipple,
    resetRipple,
    ripple,
    buttonRef,
  };
}
